import ArrowLeftIcon from '@untitled-ui/icons-react/build/esm/ArrowLeft';
import Lightbulb01Icon from '@untitled-ui/icons-react/build/esm/Lightbulb01';
import CheckIcon from '@untitled-ui/icons-react/build/esm/Check';
import PhoneIcon from '@untitled-ui/icons-react/build/esm/Phone';
import Mail01Icon from '@untitled-ui/icons-react/build/esm/Mail01';
import Clock from '@untitled-ui/icons-react/build/esm/Clock';
import { Avatar, Box, Container, Link, Stack, SvgIcon, Typography, Grid } from '@mui/material';
import { RouterLink } from 'src/components/router-link';
import { Seo } from 'src/components/seo';
import { usePageView } from 'src/hooks/use-page-view';
import { paths } from 'src/paths';
import { ContactForm } from 'src/sections/contact/contact-form';
import type { Page as PageType } from 'src/types/page';

const Page: PageType = () => {
  usePageView();

  const features = [
    'Free Consultation & Quote',
    'Same-Day Response',
    'Professional Installation',
    '5-Year Warranty',
    'Smart App Control',
    'Lifetime Support'
  ];

  const contactInfo = [
    {
      icon: <PhoneIcon />,
      title: 'Call Us',
      value: '(813) 815-0142',
      href: 'tel:+18138150142'
    },
    {
      icon: <Mail01Icon />,
      title: 'Email',
      value: 'sales@mysticmediafilm.com',
      href: 'mailto:sales@mysticmediafilm.com'
    },
    {
      icon: <Clock />,
      title: 'Hours',
      value: 'Mon-Sat: 7am-7pm',
      href: null
    }
  ];

  return (
    <>
      <Seo
        title="Contact | Mystic Media Film"
        description="Get in touch with Mystic Media Film, a Houston video production and marketing agency. Request a quote or start a project conversation with our team today."
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          background: '#ffffff',
          minHeight: '100vh',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Ambient lighting effects */}
        <Box
          sx={{
            position: 'absolute',
            top: '5%',
            right: '-10%',
            width: { xs: 400, md: 700 },
            height: { xs: 400, md: 700 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(37, 99, 235, 0.1) 0%, transparent 70%)',
            filter: 'blur(100px)',
            pointerEvents: 'none'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '40%',
            left: '-15%',
            width: { xs: 400, md: 600 },
            height: { xs: 400, md: 600 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, transparent 70%)',
            filter: 'blur(90px)',
            pointerEvents: 'none'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '10%',
            right: '20%',
            width: { xs: 300, md: 450 },
            height: { xs: 300, md: 450 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(6, 182, 212, 0.06) 0%, transparent 70%)',
            filter: 'blur(80px)',
            pointerEvents: 'none'
          }}
        />

        <Container maxWidth="lg"
sx={{ position: 'relative', zIndex: 1, py: { xs: 8, md: 10 } }}>
          <Stack spacing={6}>
            {/* Navigation */}
            <div>
              <Link
                component={RouterLink}
                href={paths.index}
                sx={{
                  alignItems: 'center',
                  display: 'inline-flex',
                  color: '#52525b',
                  '&:hover': {
                    color: '#0a0a0b'
                  }
                }}
                underline="hover"
              >
                <SvgIcon sx={{ mr: 1 }}>
                  <ArrowLeftIcon />
                </SvgIcon>
                <Typography variant="subtitle2">
                  Back to Home
                </Typography>
              </Link>
            </div>

            {/* Header */}
            <Stack spacing={3}
alignItems="center"
textAlign="center"
sx={{ px: { xs: 1, sm: 0 } }}>
              <Avatar
                sx={{
                  background: 'linear-gradient(135deg, #2563eb 0%, #06b6d4 100%)',
                  color: '#ffffff',
                  width: { xs: 56, sm: 64 },
                  height: { xs: 56, sm: 64 }
                }}
                variant="rounded"
              >
                <SvgIcon fontSize="large">
                  <Lightbulb01Icon />
                </SvgIcon>
              </Avatar>
              <Stack spacing={2}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.75rem', sm: '2rem', md: '2.75rem' },
                    color: '#0a0a0b',
                    letterSpacing: '-0.02em'
                  }}
                >
                  Let&apos;s start a project
                </Typography>
                <Typography
                  sx={{
                    color: '#52525b',
                    fontSize: { xs: '0.95rem', sm: '1rem', md: '1.125rem' },
                    maxWidth: 700,
                    lineHeight: 1.6,
                    px: { xs: 1, sm: 0 }
                  }}
                >
                  Tell us about what you&apos;re building. We&apos;ll get back to you within one business day.
                </Typography>
              </Stack>
            </Stack>

            {/* Main Content Grid */}
            <Grid container
spacing={{ xs: 3, md: 5 }}
alignItems="flex-start"
justifyContent="center">
              {/* Contact Form */}
              <Grid item
xs={12}
lg={7}>
                <Box
                  sx={{
                    p: { xs: 3, sm: 4, md: 4 },
                    borderRadius: { xs: 3, md: 4 },
                    backgroundColor: '#ffffff',
                    border: '1px solid #e4e4e7',
                    boxShadow: '0 20px 50px -20px rgba(15, 23, 42, 0.12)',
                    maxWidth: { xs: '100%', sm: 600, lg: 'none' },
                    mx: 'auto'
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '1.1rem', sm: '1.25rem' },
                      color: '#0a0a0b',
                      letterSpacing: '-0.02em',
                      mb: { xs: 2.5, sm: 3 },
                      textAlign: { xs: 'center', sm: 'left' }
                    }}
                  >
                    Tell us about your project
                  </Typography>
                  <ContactForm />
                </Box>
              </Grid>

              {/* Sidebar */}
              <Grid item
xs={12}
lg={5}>
                <Stack spacing={{ xs: 2.5, sm: 3 }}
sx={{ maxWidth: { xs: '100%', sm: 600, lg: 'none' }, mx: 'auto' }}>
                  {/* Contact Info Cards */}
                  <Box
                    sx={{
                      p: { xs: 2.5, sm: 3 },
                      borderRadius: 3,
                      backgroundColor: '#ffffff',
                      border: '1px solid #e4e4e7',
                      boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)'
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: '#0a0a0b',
                        mb: 2.5
                      }}
                    >
                      Contact Information
                    </Typography>
                    <Stack spacing={2.5}>
                      {contactInfo.map((info, index) => (
                        <Stack
                          key={index}
                          direction="row"
                          spacing={2}
                          alignItems="center"
                          component={info.href ? 'a' : 'div'}
                          href={info.href || undefined}
                          sx={{
                            textDecoration: 'none',
                            color: 'inherit',
                            p: 1.5,
                            mx: -1.5,
                            borderRadius: 2,
                            transition: 'all 0.2s ease',
                            ...(info.href && {
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: '#fafafa'
                              }
                            })
                          }}
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              backgroundColor: 'rgba(37, 99, 235, 0.1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <SvgIcon sx={{ color: '#2563eb', fontSize: 20 }}>
                              {info.icon}
                            </SvgIcon>
                          </Box>
                          <Box>
                            <Typography sx={{ color: '#71717a', fontSize: '0.8rem', mb: 0.25 }}>
                              {info.title}
                            </Typography>
                            <Typography sx={{ color: '#0a0a0b', fontWeight: 500, fontSize: '0.95rem' }}>
                              {info.value}
                            </Typography>
                          </Box>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>

                  {/* Features */}
                  <Box
                    sx={{
                      p: { xs: 2.5, sm: 3 },
                      borderRadius: 3,
                      backgroundColor: '#fafafa',
                      border: '1px solid #e4e4e7'
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: '#0a0a0b',
                        mb: 2.5
                      }}
                    >
                      What you get
                    </Typography>
                    <Stack spacing={1.5}>
                      {features.map((feature, index) => (
                        <Stack
                          key={index}
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              backgroundColor: 'rgba(37, 99, 235, 0.12)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <SvgIcon sx={{ color: '#2563eb', fontSize: 12 }}>
                              <CheckIcon />
                            </SvgIcon>
                          </Box>
                          <Typography sx={{ color: '#3f3f46', fontSize: '0.9rem' }}>
                            {feature}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>

                  {/* Trust Badge */}
                  <Box
                    sx={{
                      p: { xs: 2.5, sm: 3 },
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.06) 0%, rgba(99, 102, 241, 0.06) 100%)',
                      border: '1px solid rgba(37, 99, 235, 0.15)',
                      textAlign: 'center'
                    }}
                  >
                    <Typography sx={{ fontSize: '2rem', mb: 1 }}>
                      ✶
                    </Typography>
                    <Typography sx={{ color: '#0a0a0b', fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>
                      Made in Katy, TX
                    </Typography>
                    <Typography sx={{ color: '#52525b', fontSize: '0.85rem' }}>
                      A small studio building bold work for ambitious brands.
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default Page;
